import { captureException } from '@sentry/vue'
import { httpPostJson } from '@/lib/net'
import type {
  UserAction,
  UserActionEntityTypes,
  UserActionRequestPayload,
  UserActionResponse,
  UserActionTypes,
} from '@/services/user-action-service/types'
import { v4 as uuid } from 'uuid'
import { warn } from '@/lib/logger'

export const API_BASE_PATH = '/v1/user-actions/actions'

/**
 * Queue for managing incoming UserActions, and sending them to the ingestion API in batch on an interval
 */
export class UserActionQueue {
  public queue: UserAction[] = []
  private previousActionId: string | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private readonly interval: number = 10000

  constructor() {
    this.startTimer()
  }

  private startTimer() {
    this.timer = setInterval(() => {
      this.sendQueue()
    }, this.interval)
  }

  private clearTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  public addActionToQueue(action: UserAction) {
    this.queue.push(action)
    return this.queue
  }

  private async sendQueue() {
    if (this.queue.length === 0) {
      return
    }

    const actionsToSend = [...this.queue]
    this.queue = []

    // Chain previousActionId to first action being sent
    if (this.previousActionId) {
      actionsToSend[0].target = {
        type: 'EntryPoint',
        entryPoint: {
          id: this.previousActionId,
        },
      }
    }

    const userActionsPayload: UserActionRequestPayload = {
      actions: actionsToSend,
    }

    try {
      const response = await httpPostJson<UserActionResponse>(
        API_BASE_PATH,
        userActionsPayload
      )

      // If successful, update the previousActionId with the last action sent
      this.previousActionId = !response?.error
        ? actionsToSend[actionsToSend.length - 1].id
        : null
    } catch (e) {
      captureException(e, {
        tags: {
          userActions: `User action request failed`,
        },
      })
    }
  }

  public async sendQueueImmediately() {
    this.clearTimer()
    await this.sendQueue()
    this.startTimer()
  }
}

/**
 * Initialize UserActionQueue
 */
const userActionQueue = new UserActionQueue()

/**
 * Track a UserAction by adding it to the queue to be sent to ingestion API
 */
export const trackUserAction = ({
  actionType,
  actionDate,
  entityType,
  entityId,
  entityName,
  favoriteResultId,
}: {
  actionType: UserActionTypes
  actionDate?: Date
  entityType: UserActionEntityTypes
  entityId: string
  entityName: string
  favoriteResultId?: string
}): UserAction[] => {
  const date = actionDate || new Date()
  const startTime = date.toISOString()

  // Create generic userAction payload
  const userAction: UserAction = {
    id: uuid(),
    type: actionType,
    object: {
      type: entityType,
      id: entityId,
      name: entityName,
    },
    startTime,
  }

  // Handle for actions requiring favoriteResultId
  const actionTypesRequiringFavoriteResultId: UserActionTypes[] = [
    'BookmarkAction',
    'CancelAction',
  ]
  if (
    actionTypesRequiringFavoriteResultId.includes(actionType) &&
    !favoriteResultId
  ) {
    warn(
      'Logger',
      'Favorite result id is required for BookmarkAction and CancelAction'
    )
    return []
  } else if (favoriteResultId) {
    if (actionType === 'BookmarkAction') {
      userAction.result = {
        type: 'Favorite',
        id: favoriteResultId,
      }
    }
    if (actionType === 'CancelAction') {
      delete userAction.object
      userAction.inverseOf = {
        type: 'BookmarkAction',
        object: {
          type: entityType,
          id: entityId,
          name: entityName,
        },
        result: {
          type: 'Favorite',
          id: favoriteResultId,
        },
      }
    }
  }

  return userActionQueue.addActionToQueue(userAction)
}

/**
 * Trigger the UserActionQueue to send its queue immediately
 */
export const sendUserActionsNow = () => {
  userActionQueue.sendQueueImmediately()
}
